import { MediaAlbumListView } from '@euler/app/flows/common/components/MediaAlbumListView';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { TaskNavParams } from '@euler/app/flows/task/TaskFlow';
import { kNullVIN } from '@euler/functions/constants';
import { useCachedQuery } from '@euler/functions/useCachedQuery';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { isAlbumByYearMonth, MediaAlbum } from '@euler/model';
import { useServiceFactory } from '@euler/services/factory';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback } from 'react';

export const TaskMediaAlbumsPage = wrapNavigatorScreen(
  () => {
    const {
      taskNo,
      detail: {
        basicInfo: { licensePlateNo, vin },
      },
    } = useTaskContext();

    const {
      state: albums,
      loading,
      refreshing,
      error,
      invalidate,
    } = useMediaAlbums({ taskNo, licensePlateNo, vin });

    const navigation = useNavigation<StackNavigationProp<TaskNavParams>>();

    const onPress = useCallback(
      (album: MediaAlbum) => {
        navigation.navigate('_medias', {
          origin: `tasks/${taskNo}`,
          licensePlateNo,
          vin,
          current: album.active,
          target: isAlbumByYearMonth(album)
            ? {
                type: 'year-month',
                year: album.value.year,
                month: album.value.month,
              }
            : {
                type: 'mileage',
                mileage: album.value,
              },
        });
      },
      [licensePlateNo, navigation, vin, taskNo],
    );

    return (
      <MediaAlbumListView
        albums={albums ?? []}
        loading={loading}
        refreshing={refreshing}
        error={error}
        onRefresh={invalidate}
        onAlbumPress={onPress}
      />
    );
  },
  {
    title: '媒体图库',
  },
);

export const useMediaAlbums = ({
  licensePlateNo,
  vin,
  taskNo,
}: {
  licensePlateNo: string;
  vin: string;
  taskNo: string;
}) => {
  const { vehicleInfoService } = useServiceFactory();
  return useCachedQuery<MediaAlbum[]>(
    `tasks/${taskNo}/medias/albums.json`,
    0,
    () =>
      vin === kNullVIN
        ? vehicleInfoService.getMediaAlbumsByYearMonth({
            licensePlateNo,
            vin,
            includesCurrent: true,
          })
        : vehicleInfoService.getMediaAlbumsByMileage({
            licensePlateNo,
            vin,
            includesCurrent: true,
          }),
  );
};
